<i18n>
{
  "en": {
    "title": "Employee Wellbeing & Sustainability 2017-2018",
    "description": "Our employees make Schneider Electric a great company. We invest in ongoing learning, emphasize professional development, and advance the Health & Equity of our people on a global level. Learn how we do this, while prioritizing Diversity and Inclusion though initiatives including Gender Pay Equity and a Global Family Leave Policy to create a more sustainable future for all.",
    "ourPeople": {
      "overview": {
        "hero": {
          "title": "Our People"
        },
        "quote": {
          "title": "Executive Vice President, Global Human Resources, <br>Schneider Electric",
          "para": "Everything we do in this company is meant to create a better world. But beyond the business mission, we want to be extremely progressive, very inclusive, and want to create an environment where every single employee will have the same chance of success."
        },
        "vid": {
          "title": "Employee Well-being",
          "description": "Olivier Blum, Executive Vice President, Global Human Resources, Schneider Electric"
        },
        "para1": "At Schneider Electric, we believe it’s our great people that make us a great company. We're at our best when they are at theirs. That’s why when we talk about sustainability and energy management, we aren’t just talking about preserving energy as a resource; we are equally focused on sustaining the energy of our workforce. The energy of our employees is the vital life force that helps us progress, and it must be protected and nurtured.",
        "para2": "We invest in the energy of our people in numerous ways, including a company-wide focus on Health & Equity. A key measurement for progress is the new 2018 – 2020 Schneider Sustainability Impact target, holding us more accountable than ever. In fact, one-third of our barometer concerns health-related topics. We also prioritize Diversity and Inclusion on a global scale, advancing initiatives including Gender Pay Equity and a Global Family Leave Policy.",
        "para3": "Additionally, we keep our eye on the diverse, upcoming talent pool of tomorrow. We fostered this talent in 2017 with opportunities including Schneider Go Green in the City — an international student competition to find innovative energy solutions for smart cities, and showcasing the talent of the workforce of the future.",
        "para4": "Our ambition is that our employees everywhere have equal opportunities, while feeling uniquely valued, and safe to be at their very best. Then — and only then — can they reinvest their energy toward serving our customers and building a more sustainable future."
      }
    }
  },
  "fr": {
    "title": "Employee Wellbeing & Sustainability 2017-2018",
    "description": "Our employees make Schneider Electric a great company. We invest in ongoing learning, emphasize professional development, and advance the Health & Equity of our people on a global level. Learn how we do this, while prioritizing Diversity and Inclusion though initiatives including Gender Pay Equity and a Global Family Leave Policy to create a more sustainable future for all.",
    "ourPeople": {
      "overview": {
        "hero": {
          "title": "Nos Collaborateurs"
        },
        "quote": {
          "title": "Directeur général, Ressources humaines globales, <br>Schneider Electric",
          "para": "Chez Schneider Electric, tout ce que nous faisons a pour but de rendre le monde meilleur. Mais au-delà de notre mission d’entreprise, nous voulons faire figure d’avant-garde, agir résolument en faveur de l’inclusion et créer un environnement où prime l’égalité des chances pour l’ensemble de nos collaborateurs."
        },
        "vid": {
          "title": "Bien-être des collaborateurs",
          "description": "Olivier Blum, Directeur général Ressources humaines globales, Schneider Electric"
        },
        "para1": "Chez Schneider Electric, nous pensons que nos formidables collaborateurs font de nous une entreprise exceptionnelle. Lorsqu'ils donnent le meilleur d’eux-mêmes, notre performance est remarquable. Alors, quand nous parlons de développement durable et de gestion de l’énergie, nous pensons non seulement à la préservation de l’énergie comme ressource, mais également à la préservation de l'énergie de notre personnel. L'énergie de nos collaborateurs est la force vitale qui nous aide à aller de l’avant, nous devons donc la protéger et en prendre soin.",
        "para2": "Nous multiplions les actions en ce sens, notamment en menant dans toute l’entreprise une campagne de santé et d’équité. À cet égard, le nouveau baromètre Schneider Sustainability Impact est un indicateur clé de succès, qui nous met plus que jamais face à nos responsabilités. En effet, un tiers des critères qu’il contient sont liés à la santé. Nous donnons également la priorité à la diversité et à l’inclusion partout dans le monde, en mettant en avant des initiatives comme l’équité salariale ou encore une politique familiale globale.",
        "para3": "En outre, nous gardons un œil sur la variété des profils qui viennent alimenter le vivier de talents de demain. Ces talents, nous les avons encouragés à travers des initiatives telles que Go Green in the City : ce concours international destinés aux étudiants propose des études de cas portant sur la recherche de solutions énergétiques innovantes pour les villes intelligentes et met en avant les qualités de la main-d’œuvre de demain.",
        "para4": "Notre ambition est que, partout dans le monde, nos collaborateurs disposent des mêmes chances, tout en se sentant valorisés individuellement et libres de donner le meilleur d’eux-mêmes. C’est à cette condition – et à cette condition seulement – qu'ils pourront réinvestir leur énergie dans le service rendu à nos clients et dans la construction d'un avenir plus durable."
      }
    }
  }
}
</i18n>
<template>
  <div>
    <Hero
      :loResSrc="require('@/assets/img/homepage/our-people-low.jpg')"
      :hiResSrc="require('@/assets/img/homepage/our-people.jpg')"
      chapter="04"
      :title="$t('ourPeople.overview.hero.title')" />

    <section class="m-b-120">
      <Quote
        :photo="require('@/assets/img/our-people/our-people-overview-quote.jpg')"
        name="Olivier Blum"
        :title="$t('ourPeople.overview.quote.title')">
        <div v-html="$t('ourPeople.overview.quote.para')"></div>
      </Quote>
    </section>

    <section class="m-b-120 m-t-120">
      <YouTubeVideo videoId="byV1uM9C5TM" :title="$t('ourPeople.overview.vid.title')"
        :description="$t('ourPeople.overview.vid.description')" :preview="require('@/assets/img/thumbnails/SSDHighlightVideo_OlivierBlum_Still.jpg')" />
    </section>

    <section class="m-b-120 m-t-120">
      <Txt>
        <p>{{$t('ourPeople.overview.para1')}}</p>
        <p>{{$t('ourPeople.overview.para2')}}</p>
        <p>{{$t('ourPeople.overview.para3')}}</p>
        <p>{{$t('ourPeople.overview.para4')}}</p>
      </Txt>
    </section>

    <div class="l-content">
      <FooterNav
        nextRoute="OurPeople_CompanyValues"
        prevRoute="OurCustomers_Infrastructure" />
    </div>
  </div>
</template>
<script>
import Hero from '@/components/Hero'
import Quote from '@/components/Quote'
import YouTubeVideo from '@/components/YouTubeVideo'
import Txt from '@/components/Txt'
import FooterNav from '@/components/FooterNav'

export default {
  name: 'OurPeople_Overview',
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  mounted () {
    if (window.location.hash.substr(1).length > 0) {
      this.$nextTick(function () {
        document.getElementById(window.location.hash.substr(1)).scrollIntoView()
      })
    }
  },
  components: {
    Hero,
    Quote,
    YouTubeVideo,
    Txt,
    FooterNav
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/our-people/overview.vue